import { useCallback, useEffect, useRef, useState } from "react";
import clsx from "clsx";

// Interfaces
import { IEditProps, IExtendedEditorProps, IPredictiveTextInputProps } from "./interfaces";

// Utils
import { getReservedWords } from "./utils";
import { makeWordsBold, unsetWordsBold } from "./ExtendedTipEditor/utils";

// Components
import ExtendedTipEditor from "./ExtendedTipEditor";

// MUI
import { Stack } from "@mui/material";
import { Editor } from "@tiptap/core";

export default function PredictiveTextInput(props: Readonly<IPredictiveTextInputProps>) {
	const {
		text = "",
		placeholder = "",
		suggestion = "",
		classes = "",
		inputProps = undefined,
		sx = null,
		disabled = false,
		error = false,
		warning = false,
		onActionBlur = () => {},
		onActionFocus = () => {},
		onActionKeyDown = () => {},
		onActionPrevented = () => {},
		onActionUpdate = () => {},
		onActionTextInput = () => false,
		editorRef,
	} = props;

	const internalEditorRef = useRef<Editor | null>(null);

	// States
	const [value, setValue] = useState("");
	const words = getReservedWords();

	const updateBoldWords = useCallback(
		(editor: IExtendedEditorProps) => {
			const tr = editor.state.tr;
			tr.setMeta("isInternalUpdate", true);
			unsetWordsBold(editor.state, tr);
			makeWordsBold(editor.state, tr, words);
			editor.view.dispatch(tr);
		},
		[words]
	);

	const handleUpdate = (edit: IEditProps) => {
		const { editor, transaction } = edit;
		const isInternalUpdate = transaction.getMeta("isInternalUpdate");
		if (isInternalUpdate) {
			return;
		}
		updateBoldWords(editor);
		onActionUpdate(editor.getText());
	};

	useEffect(() => {
		setValue(text);
		return () => {
			setValue("");
		};
	}, [text]);

	return (
		<Stack
			direction="column"
			width={"100%"}
			justifyContent={"center"}
			className={clsx({
				"border border-solid border-disabled bg-white px-[10px] pb-px rounded-[6px]": true,
				"text-default": !disabled,
				"!bg-input-disabled text-neutral-gray": disabled,
				"!border-error": error,
				"!border-warning": warning && !error,
				[classes]: Boolean(classes),
			})}
			sx={{
				minHeight: "30px",
				...sx,
			}}
		>
			<ExtendedTipEditor
				editorRef={editorRef ?? internalEditorRef}
				content={value}
				placeholder={placeholder || "Search..."}
				suggestion={suggestion}
				inputProps={inputProps}
				disabled={disabled}
				onBlur={onActionBlur}
				onFocus={onActionFocus}
				onKeyDown={onActionKeyDown}
				onPreventedEvent={onActionPrevented}
				onUpdate={handleUpdate}
				onTextInput={onActionTextInput}
			/>
		</Stack>
	);
}
